import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Badge, Table, Modal, Form, Spinner } from 'react-bootstrap';
import { Globe, Shield, CheckCircle, XCircle, Clock, Copy, BoxArrowUpRight, ArrowClockwise, Gear } from 'react-bootstrap-icons';
import { useGetDNSSetupQuery, useGetVerificationStatusQuery, useEnableDKIMMutation, useValidateDNSRecordsMutation } from '../services/api';

const DNSSetup = ({ domain, onClose, hostProvider = 'aws', accountId = null }) => {
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copiedRecord, setCopiedRecord] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [editingMXRecord, setEditingMXRecord] = useState(false);
  const [mxRecordValue, setMxRecordValue] = useState('');
  const [mxRecordPriority, setMxRecordPriority] = useState('10');
  const [isSavingMX, setIsSavingMX] = useState(false);

  const { 
    data: dnsData, 
    error: dnsError, 
    isLoading: dnsLoading,
    refetch: refetchDNS
  } = useGetDNSSetupQuery(domain);

  const { 
    data: verificationData, 
    error: verificationError,
    refetch: refetchVerification
  } = useGetVerificationStatusQuery(domain);

  const [enableDKIM, { isLoading: isEnablingDKIM }] = useEnableDKIMMutation();
  const [validateDNSRecords, { isLoading: isValidatingDNS }] = useValidateDNSRecordsMutation();

  const handleCopyRecord = (record) => {
    navigator.clipboard.writeText(record);
    setCopiedRecord(record);
    setShowCopyModal(true);
    setTimeout(() => setShowCopyModal(false), 2000);
  };

  const handleVerifyDNS = async () => {
    setIsVerifying(true);
    try {
      await refetchVerification();
      await refetchDNS();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleEnableDKIM = async () => {
    try {
      await enableDKIM(domain).unwrap();
      await refetchDNS(); // Refresh DNS records to get DKIM tokens
    } catch (error) {
      console.error('Error enabling DKIM:', error);
    }
  };

  const handleValidateDNS = async () => {
    try {
      await validateDNSRecords(domain).unwrap();
      await refetchDNS(); // Refresh DNS records
      await refetchVerification(); // Refresh verification status
    } catch (error) {
      console.error('Error validating DNS:', error);
    }
  };

  const handleCopyAllRecords = () => {
    if (!dnsData?.data?.dnsRecords) return;
    
    const allRecords = [];
    const records = dnsData.data.dnsRecords;
    
    // SES Verification
    if (records.sesVerification) {
      allRecords.push(`SES Verification TXT Record:`);
      allRecords.push(`Name: ${records.sesVerification.name}`);
      allRecords.push(`Value: ${records.sesVerification.value}`);
      allRecords.push('');
    }
    
    // SPF Record
    if (records.spf) {
      allRecords.push(`SPF TXT Record:`);
      allRecords.push(`Name: ${records.spf.name}`);
      allRecords.push(`Value: ${records.spf.value}`);
      allRecords.push('');
    }
    
    // DKIM Records
    if (records.dkim && records.dkim.length > 0) {
      allRecords.push(`DKIM CNAME Records:`);
      records.dkim.forEach((dkim, index) => {
        allRecords.push(`${index + 1}. Name: ${dkim.name}`);
        allRecords.push(`   Value: ${dkim.value}`);
      });
      allRecords.push('');
    }
    
    // DMARC Record
    if (records.dmarc) {
      allRecords.push(`DMARC TXT Record:`);
      allRecords.push(`Name: ${records.dmarc.name}`);
      allRecords.push(`Value: ${records.dmarc.value}`);
      allRecords.push('');
    }

    // Email Receiving MX Record
    if (records.emailReceiving) {
      allRecords.push(`Email Receiving MX Record:`);
      allRecords.push(`Name: ${records.emailReceiving.name}`);
      allRecords.push(`Value: ${records.emailReceiving.value}`);
      allRecords.push('');
    }

    // MAIL FROM Records
    if (records.mailFrom) {
      allRecords.push(`MAIL FROM Records:`);
      if (records.mailFrom.mx) {
        allRecords.push(`MX Record:`);
        allRecords.push(`Name: ${records.mailFrom.mx.name}`);
        allRecords.push(`Value: ${records.mailFrom.mx.value}`);
        allRecords.push('');
      }
      if (records.mailFrom.spf) {
        allRecords.push(`SPF TXT Record:`);
        allRecords.push(`Name: ${records.mailFrom.spf.name}`);
        allRecords.push(`Value: ${records.mailFrom.spf.value}`);
        allRecords.push('');
      }
    }
    
    const allRecordsText = allRecords.join('\n');
    navigator.clipboard.writeText(allRecordsText);
    setCopiedRecord('All DNS Records');
    setShowCopyModal(true);
    setTimeout(() => setShowCopyModal(false), 3000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Success': return <CheckCircle className="text-success" />;
      case 'Pending': return <Clock className="text-warning" />;
      case 'Failed': return <XCircle className="text-danger" />;
      default: return <Clock className="text-muted" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Success': return <Badge bg="success">Verified</Badge>;
      case 'Pending': return <Badge bg="warning">Pending</Badge>;
      case 'Failed': return <Badge bg="danger">Failed</Badge>;
      default: return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  if (dnsLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
        <p className="mt-2">Loading DNS setup instructions...</p>
      </div>
    );
  }

  if (dnsError) {
    return (
      <Alert variant="danger">
        <XCircle className="me-2" />
        Error loading DNS setup: {dnsError.message}
      </Alert>
    );
  }

  const dnsRecords = dnsData?.data?.dnsRecords;
  const instructions = dnsData?.data?.instructions;

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3><Globe className="me-2" />DNS Setup for {domain}</h3>
              <p className="text-muted">Configure DKIM, SPF, and DMARC records for email authentication</p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="primary" onClick={handleCopyAllRecords} className="me-2">
                <Copy className="me-1" />
                📋 Copy All Records
              </Button>
              
              <Button variant="success" onClick={handleValidateDNS} disabled={isValidatingDNS}>
                {isValidatingDNS ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Validating DNS...
                  </>
                ) : (
                  <>
                    <CheckCircle className="me-1" />
                    🔍 Check DNS Status
                  </>
                )}
              </Button>
              
              <Button variant="outline-primary" onClick={handleVerifyDNS} disabled={isVerifying}>
                {isVerifying ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <ArrowClockwise className="me-1" />
                    Verify DNS
                  </>
                )}
              </Button>
              
              {dnsRecords?.dkim?.length === 0 && (
                <Button 
                  variant="outline-success" 
                  onClick={handleEnableDKIM} 
                  disabled={isEnablingDKIM}
                >
                  {isEnablingDKIM ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Enabling...
                    </>
                  ) : (
                    <>
                      <Shield className="me-1" />
                      Enable DKIM
                    </>
                  )}
                </Button>
              )}
              
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Smart Manual Setup Instructions */}
      <Row className="mb-4">
        <Col>
          <Alert variant="info">
            <Shield className="me-2" />
            <strong>📋 Easy Manual Setup:</strong> 
            <ol className="mb-0 mt-2">
              <li><strong>Click "📋 Copy All Records"</strong> to copy all DNS records at once</li>
              <li><strong>Go to your DNS provider</strong> (GoDaddy, Cloudflare, Namecheap, etc.)</li>
              <li><strong>Add each DNS record</strong> from the copied text</li>
              <li><strong>Click "🔍 Check DNS Status"</strong> to verify everything is working</li>
            </ol>
          </Alert>
        </Col>
      </Row>

      {/* Current Status */}
      {verificationData?.data && (
        <Row className="mb-4">
          <Col>
            <Card className="border-info">
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">
                  <Shield className="me-2" />
                  Current Verification Status
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <div className="text-center">
                      <h6>Domain Verification</h6>
                      {getStatusIcon(verificationData.data.verification.status)}
                      <div className="mt-1">
                        {getStatusBadge(verificationData.data.verification.status)}
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <h6>DKIM Status</h6>
                      {getStatusIcon(verificationData.data.dkim.status)}
                      <div className="mt-1">
                        {getStatusBadge(verificationData.data.dkim.status)}
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <h6>Overall Status</h6>
                      {getStatusIcon(verificationData.data.overallStatus.readyForEmail ? 'Success' : 'Pending')}
                      <div className="mt-1">
                        {getStatusBadge(verificationData.data.overallStatus.readyForEmail ? 'Ready' : 'Pending')}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* DNS Records Setup */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Required DNS Records</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Record Type</th>
                    <th>Name</th>
                    <th>Value</th>
                    <th>Purpose</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* SES Verification Record */}
                  {dnsRecords?.sesVerification && (
                    <tr>
                      <td>
                        <Badge bg="primary">TXT</Badge>
                      </td>
                      <td>
                        <code>{dnsRecords.sesVerification.name}</code>
                      </td>
                      <td>
                        <code className="text-break">{dnsRecords.sesVerification.value}</code>
                      </td>
                      <td>
                        <small className="text-muted">{dnsRecords.sesVerification.purpose}</small>
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleCopyRecord(dnsRecords.sesVerification.value)}
                        >
                          <Copy className="me-1" />
                          Copy
                        </Button>
                      </td>
                    </tr>
                  )}

                  {/* SPF Record */}
                  {dnsRecords?.spf && (
                    <tr>
                      <td>
                        <Badge bg="info">TXT</Badge>
                      </td>
                      <td>
                        <code>{dnsRecords.spf.name}</code>
                      </td>
                      <td>
                        <code className="text-break">{dnsRecords.spf.value}</code>
                      </td>
                      <td>
                        <small className="text-muted">{dnsRecords.spf.purpose}</small>
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleCopyRecord(dnsRecords.spf.value)}
                        >
                          <Copy className="me-1" />
                          Copy
                        </Button>
                      </td>
                    </tr>
                  )}

                  {/* DKIM Records */}
                  {dnsRecords?.dkim?.map((dkimRecord, index) => (
                    <tr key={index}>
                      <td>
                        <Badge bg="success">CNAME</Badge>
                      </td>
                      <td>
                        <code>{dkimRecord.name}</code>
                      </td>
                      <td>
                        <code className="text-break">{dkimRecord.value}</code>
                      </td>
                      <td>
                        <small className="text-muted">{dkimRecord.purpose}</small>
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleCopyRecord(dkimRecord.value)}
                        >
                          <Copy className="me-1" />
                          Copy
                        </Button>
                      </td>
                    </tr>
                  ))}

                  {/* DMARC Record */}
                  {dnsRecords?.dmarc && (
                    <tr>
                      <td>
                        <Badge bg="warning">TXT</Badge>
                      </td>
                      <td>
                        <code>{dnsRecords.dmarc.name}</code>
                      </td>
                      <td>
                        <code className="text-break">{dnsRecords.dmarc.value}</code>
                      </td>
                      <td>
                        <small className="text-muted">{dnsRecords.dmarc.purpose}</small>
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleCopyRecord(dnsRecords.dmarc.value)}
                        >
                          <Copy className="me-1" />
                          Copy
                        </Button>
                      </td>
                    </tr>
                  )}

                  {/* Email Receiving MX Record */}
                  {dnsRecords?.emailReceiving && (
                    <tr>
                      <td>
                        <Badge bg="success">MX</Badge>
                      </td>
                      <td>
                        <code>{dnsRecords.emailReceiving.name}</code>
                      </td>
                      <td>
                        {editingMXRecord ? (
                          <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                              type="number"
                              size="sm"
                              style={{ width: '80px' }}
                              value={mxRecordPriority}
                              onChange={(e) => setMxRecordPriority(e.target.value)}
                              placeholder="10"
                            />
                            <Form.Control
                              type="text"
                              size="sm"
                              style={{ flex: 1 }}
                              value={mxRecordValue}
                              onChange={(e) => setMxRecordValue(e.target.value)}
                              placeholder="inbound-smtp.us-east-1.amazonaws.com"
                            />
                          </div>
                        ) : (
                          <code className="text-break">{dnsRecords.emailReceiving.value}</code>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{dnsRecords.emailReceiving.purpose}</small>
                      </td>
                      <td>
                        {editingMXRecord ? (
                          <div className="d-flex gap-1">
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={async () => {
                                setIsSavingMX(true);
                                try {
                                  // Get API URL (centralized, custom domain)
                                  const apiModule = await import('../services/api');
                                  const apiUrl = apiModule.getApiUrl();
                                  
                                  // Get auth token
                                  await apiModule.waitForAuthFunctions();
                                  const tokenGetter = apiModule.getTokenGetter();
                                  const token = await tokenGetter?.() || '';
                                  
                                  // Update MX record
                                  const response = await fetch(`${apiUrl}/domains/${domain}/mx-record`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify({
                                      priority: parseInt(mxRecordPriority),
                                      value: mxRecordValue,
                                      hostProvider: hostProvider,
                                      ...(accountId && { accountId: accountId })
                                    })
                                  });
                                  
                                  if (response.ok) {
                                    setEditingMXRecord(false);
                                    await refetchDNS();
                                    alert('MX record updated successfully!');
                                  } else {
                                    const error = await response.json();
                                    alert(`Failed to update MX record: ${error.message || 'Unknown error'}`);
                                  }
                                } catch (error) {
                                  console.error('Error updating MX record:', error);
                                  alert(`Failed to update MX record: ${error.message || 'Unknown error'}`);
                                } finally {
                                  setIsSavingMX(false);
                                }
                              }}
                              disabled={isSavingMX || !mxRecordValue.trim()}
                            >
                              {isSavingMX ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="me-1" />
                                  Save
                                </>
                              )}
                            </Button>
                            <Button 
                              variant="secondary" 
                              size="sm"
                              onClick={() => {
                                setEditingMXRecord(false);
                                // Reset to original values
                                if (dnsRecords.emailReceiving.value) {
                                  const parts = dnsRecords.emailReceiving.value.split(' ');
                                  if (parts.length >= 2) {
                                    setMxRecordPriority(parts[0]);
                                    setMxRecordValue(parts.slice(1).join(' '));
                                  }
                                }
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex gap-1">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleCopyRecord(dnsRecords.emailReceiving.value)}
                            >
                              <Copy className="me-1" />
                              Copy
                            </Button>
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              onClick={() => {
                                setEditingMXRecord(true);
                                // Parse current MX record value (format: "10 inbound-smtp.us-east-1.amazonaws.com")
                                if (dnsRecords.emailReceiving.value) {
                                  const parts = dnsRecords.emailReceiving.value.split(' ');
                                  if (parts.length >= 2) {
                                    setMxRecordPriority(parts[0]);
                                    setMxRecordValue(parts.slice(1).join(' '));
                                  } else {
                                    setMxRecordPriority('10');
                                    setMxRecordValue(dnsRecords.emailReceiving.value);
                                  }
                                } else {
                                  setMxRecordPriority('10');
                                  setMxRecordValue('');
                                }
                              }}
                            >
                              <Gear className="me-1" />
                              Edit
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* MAIL FROM Records */}
                  {dnsRecords?.mailFrom && (
                    <>
                      {/* MAIL FROM MX Record */}
                      {dnsRecords.mailFrom.mx && (
                        <tr>
                          <td>
                            <Badge bg="info">MX</Badge>
                          </td>
                          <td>
                            <code>{dnsRecords.mailFrom.mx.name}</code>
                          </td>
                          <td>
                            <code className="text-break">{dnsRecords.mailFrom.mx.value}</code>
                          </td>
                          <td>
                            <small className="text-muted">{dnsRecords.mailFrom.mx.purpose}</small>
                          </td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleCopyRecord(dnsRecords.mailFrom.mx.value)}
                            >
                              <Copy className="me-1" />
                              Copy
                            </Button>
                          </td>
                        </tr>
                      )}
                      
                      {/* MAIL FROM SPF Record */}
                      {dnsRecords.mailFrom.spf && (
                        <tr>
                          <td>
                            <Badge bg="info">TXT</Badge>
                          </td>
                          <td>
                            <code>{dnsRecords.mailFrom.spf.name}</code>
                          </td>
                          <td>
                            <code className="text-break">{dnsRecords.mailFrom.spf.value}</code>
                          </td>
                          <td>
                            <small className="text-muted">{dnsRecords.mailFrom.spf.purpose}</small>
                          </td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleCopyRecord(dnsRecords.mailFrom.spf.value)}
                            >
                              <Copy className="me-1" />
                              Copy
                            </Button>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Setup Instructions */}
      <Row className="mb-4">
        <Col>
          <Card className="border-success">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                <Shield className="me-2" />
                Step-by-Step Setup Instructions
              </h5>
            </Card.Header>
            <Card.Body>
              <ol className="mb-0">
                <li className="mb-2">
                  <strong>Add SES Verification Record:</strong> Copy the TXT record above and add it to your DNS provider (Route 53, Cloudflare, etc.)
                </li>
                <li className="mb-2">
                  <strong>Add SPF Record:</strong> Copy the SPF TXT record to authorize Amazon SES to send emails from your domain
                </li>
                <li className="mb-2">
                  <strong>Add DKIM Records:</strong> Copy all 3 DKIM CNAME records for email authentication
                </li>
                <li className="mb-2">
                  <strong>Add DMARC Record:</strong> Copy the DMARC TXT record for email policy enforcement
                </li>
                {dnsRecords?.mailFrom && (
                  <li className="mb-2">
                    <strong>Add MAIL FROM Records:</strong> Copy the MAIL FROM MX and TXT (SPF) records for SPF alignment (if configured)
                  </li>
                )}
                <li className="mb-2">
                  <strong>Wait for Propagation:</strong> DNS changes can take 5-10 minutes to propagate
                </li>
                <li className="mb-2">
                  <strong>Verify Setup:</strong> Click "Verify DNS" button to check if all records are working
                </li>
              </ol>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Help Links */}
      <Row>
        <Col>
          <Card className="border-light">
            <Card.Body>
              <h6>Need Help?</h6>
              <div className="d-flex gap-3">
                <Button variant="outline-primary" size="sm" href="https://docs.aws.amazon.com/ses/latest/dg/verify-domains.html" target="_blank">
                  <BoxArrowUpRight className="me-1" />
                  AWS SES Documentation
                </Button>
                <Button variant="outline-secondary" size="sm" href="https://docs.aws.amazon.com/ses/latest/dg/send-email-authentication-dkim.html" target="_blank">
                  <BoxArrowUpRight className="me-1" />
                  DKIM Setup Guide
                </Button>
                <Button variant="outline-info" size="sm" href="https://docs.aws.amazon.com/ses/latest/dg/send-email-authentication-spf.html" target="_blank">
                  <BoxArrowUpRight className="me-1" />
                  SPF Setup Guide
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Copy Success Modal */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} size="sm" centered>
        <Modal.Body className="text-center py-4">
          <CheckCircle className="text-success mb-3" size={48} />
          <h5>Copied to Clipboard!</h5>
          <p className="text-muted mb-0">DNS record value has been copied to your clipboard.</p>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default DNSSetup;
